import React, { useEffect, useRef, useState } from "react";
import { CCard, CCardBody, CButton } from "@coreui/react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import * as echarts from "echarts";

const PoliciesCard = ({
  policiesData,
  dateRange,
  setDateRange,
  totalRegisteredUsers,
  registeredUsersPercentageDifference,
  policiesPercentageDifference,
}) => {
  const chartRef = useRef(null);
  const dropdownRef = useRef(null);

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [activeButton, setActiveButton] = useState(null);
  const [totalPolicies, setTotalPolicies] = useState(0);

  const handleDateChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      setTempDateRange(selectedDates);
      setActiveButton(null);
    }
    // Prevent dropdown from closing
    setDropdownVisible(true);
  };

  const applyDateRange = () => {
    setDateRange(tempDateRange);
    setDropdownVisible(false);
  };

  const setDateRangeToToday = () => {
    const today = new Date();
    setTempDateRange([today, today]);
  };

  const setDateRangeToThisWeek = () => {
    const today = new Date();
    const firstDayOfWeek = new Date(
      today.setDate(today.getDate() - today.getDay())
    );
    const lastDayOfWeek = new Date(
      today.setDate(today.getDate() - today.getDay() + 6)
    );
    setTempDateRange([firstDayOfWeek, lastDayOfWeek]);
  };

  const setDateRangeToThisMonth = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    );
    setTempDateRange([firstDayOfMonth, lastDayOfMonth]);
  };

  const resetDateRange = () => {
    const start = new Date();
    start.setMonth(start.getMonth() - 5);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);

    const end = new Date();
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    end.setHours(23, 59, 59, 999);

    setTempDateRange([start, end]);
  };

  const handleButtonClick = (rangeSetter, buttonId) => {
    rangeSetter();
    setActiveButton(buttonId);
  };

  const formatDateRange = (range) => {
    if (range.length === 2) {
      return `${range[0]
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, "-")} - ${range[1]
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, "-")}`.toUpperCase();
    }
    return "Select Date Range";
  };

  const calculateTotalPolicies = (data) => {
    if (
      !data ||
      !data.datasets ||
      !data.datasets[0] ||
      !data.datasets[0].data
    ) {
      return 0;
    }
    return data.datasets.reduce(
      (acc, dataset) =>
        acc + dataset.data.reduce((sum, value) => sum + value, 0),
      0
    );
  };

  useEffect(() => {
    if (!policiesData || !policiesData.labels || !policiesData.datasets) return;

    setTotalPolicies(calculateTotalPolicies(policiesData));

    const chartInstance = echarts.init(chartRef.current);
    const options = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      grid: {
        left: "4%",
        right: "5%",
        bottom: "20%",
        containLabel: true,
      },
      legend: {
        data: policiesData.datasets.map((dataset) => dataset.label),
        width: 300,
        bottom: 0,
        selectedMode: "multiple",
        selected: policiesData.datasets.reduce((acc, dataset) => {
          acc[dataset.label] = true;
          return acc;
        }, {}),
      },
      xAxis: {
        type: "category",
        data: policiesData.labels,
      },
      yAxis: {
        type: "value",
      },
      series: policiesData.datasets.map((dataset) => ({
        type: "bar",
        data: dataset.data,
        name: dataset.label,
        itemStyle: {
          color: dataset.backgroundColor,
          barBorderRadius: [10, 10, 0, 0],
        },
        barWidth: "15%",
      })),
      barCategoryGap: "50%",
    };
    chartInstance.setOption(options);

    chartInstance.on("legendselectchanged", function (params) {
      const selected = params.selected;
      const newTotal = policiesData.datasets.reduce((sum, dataset) => {
        return selected[dataset.label]
          ? sum + dataset.data.reduce((acc, value) => acc + value, 0)
          : sum;
      }, 0);
      setTotalPolicies(newTotal);
    });

    return () => {
      chartInstance.dispose();
    };
  }, [policiesData]);

  return (
    <CCard className="border-light">
      <CCardBody style={{ overflowX: "auto" }}>
        <span>Number of Policies</span>
        <div className="p-2">
          <div>
            <h3>
              <strong>{totalPolicies}</strong>
            </h3>
            <span className="text-muted">Total Number of Policies</span>
          </div>

          <div
            ref={dropdownRef}
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "4px",
              borderRadius: "5px",
              width: "300px",
              margin: "10px auto",
              marginBottom: "-20px",
              position: "relative",
            }}
          >
            <CButton
              style={{
                border: "0px solid lightgrey",
                backgroundColor: "#F8F5F5",
                borderRadius: "5px",
                color: "#343333",
                height: "45px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                transition: "background-color 0.3s",
              }}
              color="primary"
              onClick={() => setDropdownVisible(!dropdownVisible)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#e0e0e0";
                const icon = e.target.querySelector("i");
                if (icon) {
                  icon.style.backgroundColor = "#e0e0e0";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#F8F5F5";
                const icon = e.target.querySelector("i");
                if (icon) {
                  icon.style.backgroundColor = "#F8F5F5";
                }
              }}
            >
              {formatDateRange(dateRange)}
              <i
                className={`bi bi-chevron-down ${
                  dropdownVisible ? "rotate" : ""
                }`}
                style={{ marginLeft: "10px" }}
              ></i>
            </CButton>
            {dropdownVisible && (
              <CCard
                className="border-light"
                style={{
                  position: "absolute",
                  top: "40px",
                  zIndex: 1000,
                  width: "300px",
                }}
              >
                <CCardBody style={{ overflowX: "auto" }}>
                  <span>Filter By Date Range</span>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-start",
                      padding: "8px",
                      borderRadius: "5px",
                      width: "100%",
                      margin: "0px auto",
                      position: "relative",
                    }}
                  >
                    <Flatpickr
                      options={{ mode: "range", dateFormat: "d/m/Y" }}
                      value={tempDateRange}
                      onChange={handleDateChange}
                      className="form-control"
                      style={{
                        border: "1px solid lightgrey",
                        backgroundColor: "white",
                        color: "#555E67",
                        borderRadius: "10px",
                        width: "100%",
                        height: "35px",
                        transition: "background-color 0.3s",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = "#e0e0e0")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = "white")
                      }
                    />
                    <i
                      className={`bi bi-calendar`}
                      style={{
                        position: "absolute",
                        right: "20px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    ></i>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                      marginTop: "0px",
                    }}
                  >
                    <CButton
                      color="secondary"
                      onClick={() =>
                        handleButtonClick(setDateRangeToToday, "today")
                      }
                      style={{
                        border: "1px solid lightgrey",
                        backgroundColor:
                          activeButton === "today" ? "#0B4FE4" : "white",
                        borderRadius: "10px",
                        color: activeButton === "today" ? "white" : "#555E67",
                        width: "30%",
                        height: "60px",
                        cursor: "pointer",
                        transition: "background-color 0.3s",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor =
                          activeButton === "today" ? "#0056b3" : "#e0e0e0")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor =
                          activeButton === "today" ? "#007bff" : "white")
                      }
                    >
                      Today
                    </CButton>
                    <CButton
                      color="secondary"
                      onClick={() =>
                        handleButtonClick(setDateRangeToThisWeek, "thisWeek")
                      }
                      style={{
                        border: "1px solid lightgrey",
                        backgroundColor:
                          activeButton === "thisWeek" ? "#0B4FE4" : "white",
                        borderRadius: "10px",
                        color:
                          activeButton === "thisWeek" ? "white" : "#555E67",
                        width: "30%",
                        height: "60px",
                        cursor: "pointer",
                        transition: "background-color 0.3s",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor =
                          activeButton === "thisWeek" ? "#0056b3" : "#e0e0e0")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor =
                          activeButton === "thisWeek" ? "#007bff" : "white")
                      }
                    >
                      This Week
                    </CButton>
                    <CButton
                      color="secondary"
                      onClick={() =>
                        handleButtonClick(setDateRangeToThisMonth, "thisMonth")
                      }
                      style={{
                        border: "1px solid lightgrey",
                        backgroundColor:
                          activeButton === "thisMonth" ? "#0B4FE4" : "white",
                        borderRadius: "10px",
                        color:
                          activeButton === "thisMonth" ? "white" : "#555E67",
                        width: "30%",
                        height: "60px",
                        cursor: "pointer",
                        transition: "background-color 0.3s",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor =
                          activeButton === "thisMonth" ? "#0056b3" : "#e0e0e0")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor =
                          activeButton === "thisMonth" ? "#007bff" : "white")
                      }
                    >
                      This Month
                    </CButton>
                  </div>
                  <hr></hr>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "10px",
                    }}
                  >
                    <CButton
                      color="secondary"
                      onClick={() => {
                        resetDateRange();
                        setActiveButton(null);
                      }}
                      style={{
                        border: "1px solid #0B4FE4",
                        backgroundColor: "white",
                        borderRadius: "5px",
                        color: "#0B4FE4",
                        width: "45%",
                        height: "40px",
                        cursor: "pointer",
                        transition: "background-color 0.3s",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = "#e0e0e0")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = "white")
                      }
                    >
                      Reset
                    </CButton>
                    <CButton
                      color="primary"
                      onClick={applyDateRange}
                      style={{
                        border: "1px solid lightgrey",
                        backgroundColor: "#0B4FE4",
                        borderRadius: "5px",
                        color: "white",
                        width: "45%",
                        height: "40px",
                        cursor: "pointer",
                        transition: "background-color 0.3s",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = "#083bb3")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = "#0B4FE4")
                      }
                    >
                      Apply
                    </CButton>
                  </div>
                </CCardBody>
              </CCard>
            )}
          </div>
          {policiesData && policiesData.labels && policiesData.datasets && (
            <div
              ref={chartRef}
              style={{ height: "350px", width: "350px" }}
            ></div>
          )}
        </div>
      </CCardBody>
    </CCard>
  );
};

export default PoliciesCard;
